import { Component, EventEmitter, Input, Output, OnInit, OnChanges, SimpleChanges } from '@angular/core';
import { FileUploadService, FileUploadData, FileValidationResult } from '../../services/file-upload.service';

@Component({
  selector: 'app-file-upload',
  templateUrl: './file-upload.component.html',
  styleUrls: ['./file-upload.component.css']
})
export class FileUploadComponent implements OnInit, OnChanges {

  @Input() isVisible: boolean = false;
  @Input() isRequired: boolean = false;
  @Output() filesChange = new EventEmitter<FileUploadData>();
  @Output() validationChange = new EventEmitter<boolean>();

  selectedDocuments: File[] = [];
  selectedMidia: File | null = null;
  documentErrors: string[] = [];
  midiaErrors: string[] = [];
  isDragOver: boolean = false;
  isUploading: boolean = false;

  constructor(
    private fileUploadService: FileUploadService
  ) {}

  ngOnInit(): void {
    // Aplicar validações condicionais baseadas em isRequired
    this.updateValidators();
    // Emitir estado inicial
    this.emitChanges();
    
    // Forçar emissão após inicialização completa
    setTimeout(() => {
      this.emitChanges();
    }, 100);
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.updateValidators();
    // Emitir estado atual quando propriedades mudam
    this.emitChanges();
    
    // Forçar emissão após mudanças de propriedades
    setTimeout(() => {
      this.emitChanges();
    }, 100);
  }

  private updateValidators(): void {
    // Como removemos o FormGroup, não precisamos mais atualizar validadores
    // A validação é feita diretamente nos arrays selectedDocuments e selectedVideo
  }

  /**
   * Manipula a seleção de documentos
   */
  onDocumentSelect(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const files = Array.from(input.files);
      this.processDocuments(files);
    }
  }

  /**
   * Manipula a seleção de mídia (áudio ou vídeo)
   */
  onMidiaSelect(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      this.processMidia(input.files[0]);
    }
  }

  /**
   * Processa os documentos selecionados
   */
  private processDocuments(files: File[]): void {
    const validation = this.fileUploadService.validateDocuments(files);
    
    if (validation.isValid) {
      this.selectedDocuments = files;
      this.documentErrors = [];
    } else {
      this.documentErrors = validation.errors;
      this.selectedDocuments = [];
    }

    // Emitir mudanças imediatamente
    this.emitChanges();
    
    // Forçar uma segunda emissão após um pequeno delay para garantir que os eventos sejam capturados
    setTimeout(() => {
      this.emitChanges();
    }, 100);
  }

  /**
   * Processa o arquivo de mídia selecionado
   */
  private processMidia(file: File): void {
    const validation = this.fileUploadService.validateMedia(file, false);
    
    if (validation.isValid) {
      this.selectedMidia = file;
      this.midiaErrors = [];
    } else {
      this.midiaErrors = validation.errors;
      this.selectedMidia = null;
    }

    // Emitir mudanças imediatamente
    this.emitChanges();
    
    // Forçar uma segunda emissão após um pequeno delay
    setTimeout(() => {
      this.emitChanges();
    }, 100);
  }

  /**
   * Remove um documento específico
   */
  removeDocument(index: number): void {
    this.selectedDocuments.splice(index, 1);
    
    // Revalidar após remoção
    if (this.selectedDocuments.length > 0) {
      const validation = this.fileUploadService.validateDocuments(this.selectedDocuments);
      this.documentErrors = validation.errors;
    } else {
      this.documentErrors = this.isRequired ? ['É obrigatório enviar pelo menos um documento'] : [];
    }

    // Emitir mudanças imediatamente
    this.emitChanges();
    
    // Forçar uma segunda emissão após um pequeno delay
    setTimeout(() => {
      this.emitChanges();
    }, 100);
  }

  /**
   * Remove o arquivo de mídia selecionado
   */
  removeMidia(): void {
    this.selectedMidia = null;
    this.midiaErrors = [];
    
    // Emitir mudanças imediatamente
    this.emitChanges();
    
    // Forçar uma segunda emissão após um pequeno delay
    setTimeout(() => {
      this.emitChanges();
    }, 100);
  }

  /**
   * Manipula eventos de drag and drop
   */
  onDragOver(event: DragEvent): void {
    event.preventDefault();
    this.isDragOver = true;
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    this.isDragOver = false;
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    this.isDragOver = false;

    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      const fileArray = Array.from(files);
      
      // Separar arquivos por tipo
      const documents = fileArray.filter(file => 
        this.fileUploadService.isPdfFile(file) || 
        this.fileUploadService.isImageFile(file) || 
        this.fileUploadService.isDocumentFile(file)
      );
      
      const media = fileArray.filter(file => 
        this.fileUploadService.isVideoFile(file) ||
        this.fileUploadService.isAudioFile(file)
      );

      if (documents.length > 0) {
        this.processDocuments(documents);
      }

      if (media.length > 0 && !this.selectedMidia) {
        this.processMidia(media[0]); // Apenas o primeiro arquivo de mídia
      }
    }
  }

  /**
   * Emite as mudanças para o componente pai
   */
  private emitChanges(): void {
    const fileData: FileUploadData = {
      documentos: this.selectedDocuments,
      midia: this.selectedMidia
    };

    const isValid = this.isFormValid();

    this.filesChange.emit(fileData);
    this.validationChange.emit(isValid);
  }

  /**
   * Verifica se o formulário está válido
   */
  private isFormValid(): boolean {
    if (!this.isRequired || !this.isVisible) {
      return true;
    }

    // Documentos são obrigatórios
    const hasValidDocuments = this.selectedDocuments.length > 0 && this.documentErrors.length === 0;
    
    // Mídia é opcional, mas se tiver algum erro, invalida o formulário
    const midiaIsValid = this.midiaErrors.length === 0;

    return hasValidDocuments && midiaIsValid;
  }

  /**
   * Obtém o ícone baseado no tipo de arquivo
   */
  getFileIcon(file: File): string {
    if (this.fileUploadService.isPdfFile(file)) {
      return 'fa-file-pdf-o';
    } else if (this.fileUploadService.isImageFile(file)) {
      return 'fa-file-image-o';
    } else if (this.fileUploadService.isDocumentFile(file)) {
      return 'fa-file-word-o';
    } else if (this.fileUploadService.isVideoFile(file)) {
      return 'fa-file-video-o';
    } else if (this.fileUploadService.isAudioFile(file)) {
      return 'fa-file-audio-o';
    }
    return 'fa-file-o';
  }

  /**
   * Formata o tamanho do arquivo
   */
  formatFileSize(bytes: number): string {
    return this.fileUploadService.formatFileSize(bytes);
  }

  /**
   * Reseta o componente
   */
  reset(): void {
    this.selectedDocuments = [];
    this.selectedMidia = null;
    this.documentErrors = [];
    this.midiaErrors = [];
    
    // Emitir mudanças imediatamente
    this.emitChanges();
    
    // Forçar uma segunda emissão após um pequeno delay
    setTimeout(() => {
      this.emitChanges();
    }, 100);
  }

  /**
   * Verifica se há arquivos selecionados
   */
  hasFiles(): boolean {
    return this.selectedDocuments.length > 0 || this.selectedMidia !== null;
  }

}
